import { useState, useEffect } from "react";
import { Routes, Route, Link, useNavigate, useParams } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { NativeSelect } from "@/components/ui/native-select";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Plus, ArrowLeft, Edit, Trash2, Briefcase, DollarSign, TrendingUp, Loader2, Search } from "lucide-react";
import { extractErrorMessage } from "@/lib/errorMessage";

function CargosList() {
  const [cargos, setCargos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showReajuste, setShowReajuste] = useState(false);
  const [reajusteTipo, setReajusteTipo] = useState("global");
  const [reajustePerc, setReajustePerc] = useState("");
  const [selectedCargos, setSelectedCargos] = useState([]);
  const [applying, setApplying] = useState(false);
  const token = sessionStorage.getItem("luvcorp_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchCargos(); }, []);

  const fetchCargos = async () => {
    try {
      const res = await axios.get(`${API}/cargos`, { headers });
      setCargos(res.data);
    } catch { toast.error("Erro ao carregar cargos"); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/cargos/${id}`, { headers });
      toast.success("Cargo excluido");
      fetchCargos();
    } catch { toast.error("Erro ao excluir"); }
  };

  const handleReajuste = async () => {
    if (!reajustePerc || parseFloat(reajustePerc) === 0) {
      toast.error("Informe o percentual de reajuste");
      return;
    }
    if (reajusteTipo !== "global" && selectedCargos.length === 0) {
      toast.error("Selecione ao menos um cargo");
      return;
    }
    setApplying(true);
    try {
      const payload = {
        tipo: reajusteTipo,
        percentual: parseFloat(reajustePerc),
        cargo_ids: reajusteTipo === "global" ? [] : selectedCargos,
      };
      const res = await axios.post(`${API}/cargos/reajuste-salarial`, payload, { headers });
      toast.success(res.data.message);
      setShowReajuste(false);
      setReajustePerc("");
      setSelectedCargos([]);
      fetchCargos();
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao aplicar reajuste")); }
    finally { setApplying(false); }
  };

  const toggleCargoSelection = (id) => {
    setSelectedCargos(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const filtered = cargos.filter(c =>
    c.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    String(c.nivel).includes(searchTerm)
  );

  // Group by cargo name for display
  const grouped = {};
  filtered.forEach(c => {
    if (!grouped[c.nome]) grouped[c.nome] = [];
    grouped[c.nome].push(c);
  });

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-[#0A1628]" /></div>;

  return (
    <div className="space-y-4" data-testid="cargos-list">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-heading font-bold text-[#0A1628]">Cadastro de Cargos</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowReajuste(true)} data-testid="btn-reajuste">
            <TrendingUp className="h-4 w-4 mr-2" />Reajuste Salarial
          </Button>
          <Link to="/cadastros/cargos/new">
            <Button className="bg-[#0A1628] hover:bg-[#0A1628]/90" data-testid="btn-novo-cargo">
              <Plus className="h-4 w-4 mr-2" />Novo Cargo
            </Button>
          </Link>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
        <Input placeholder="Buscar por nome ou nivel..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" />
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cargo</TableHead>
                <TableHead>Descricao</TableHead>
                <TableHead className="text-center">Nivel</TableHead>
                <TableHead className="text-right">Salario Base</TableHead>
                <TableHead className="text-right">Custo Diário</TableHead>
                <TableHead className="text-right">Acoes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(cargo => (
                <TableRow key={cargo.id}>
                  <TableCell className="font-medium text-[#0A1628]">{cargo.nome}</TableCell>
                  <TableCell className="text-slate-500 max-w-xs truncate text-sm">{cargo.descricao || "-"}</TableCell>
                  <TableCell className="text-center"><Badge variant="outline" className="bg-[#0A1628]/5">{cargo.nivel}</Badge></TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {cargo.salario_base ? <span className="text-emerald-600 font-medium">R$ {Number(cargo.salario_base).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</span> : <span className="text-slate-400">-</span>}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {cargo.custo_diario ? <span className="text-luvcorp-navy font-semibold">R$ {Number(cargo.custo_diario).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</span> : <span className="text-slate-400">-</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Link to={`/cadastros/cargos/${cargo.id}/edit`}><Button variant="ghost" size="sm"><Edit className="h-4 w-4" /></Button></Link>
                      <AlertDialog>
                        <AlertDialogTrigger asChild><Button variant="ghost" size="sm" className="text-red-500"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Excluir cargo?</AlertDialogTitle><AlertDialogDescription>Esta acao nao pode ser desfeita.</AlertDialogDescription></AlertDialogHeader>
                          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(cargo.id)} className="bg-red-600">Excluir</AlertDialogAction></AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filtered.length === 0 && <div className="p-12 text-center"><Briefcase className="h-12 w-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">Nenhum cargo cadastrado</p></div>}
        </CardContent>
      </Card>

      {/* Dialog Reajuste */}
      <Dialog open={showReajuste} onOpenChange={setShowReajuste}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0A1628]"><TrendingUp className="h-5 w-5 text-[#C9A84C]" />Reajuste Salarial</DialogTitle>
            <DialogDescription>Aplique reajuste percentual aos salarios base dos cargos.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Tipo de Reajuste</Label>
              <NativeSelect value={reajusteTipo} onValueChange={v => { setReajusteTipo(v); setSelectedCargos([]); }} options={[
                { value: "global", label: "Global - Todos os cargos" },
                { value: "multiplo", label: "Seletivo - Escolher cargos" },
              ]} data-testid="select-tipo-reajuste" />
            </div>
            <div className="space-y-2">
              <Label>Percentual de Reajuste (%)</Label>
              <Input type="number" step="0.01" value={reajustePerc} onChange={e => setReajustePerc(e.target.value)} placeholder="Ex: 5.5" data-testid="input-percentual" />
            </div>
            {reajusteTipo !== "global" && (
              <div className="space-y-2">
                <Label>Selecione os Cargos</Label>
                <div className="max-h-48 overflow-y-auto border rounded p-2 space-y-1">
                  {cargos.filter(c => c.salario_base).map(c => (
                    <label key={c.id} className="flex items-center gap-2 p-1.5 hover:bg-slate-50 rounded cursor-pointer text-sm">
                      <Checkbox checked={selectedCargos.includes(c.id)} onCheckedChange={() => toggleCargoSelection(c.id)} />
                      <span className="flex-1">{c.nome} (Nivel {c.nivel})</span>
                      <span className="text-xs text-slate-500 font-mono">R$ {Number(c.salario_base).toFixed(2)}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {reajustePerc && (
              <div className="p-3 bg-[#C9A84C]/10 rounded text-sm">
                <p className="font-medium text-[#0A1628]">Preview do reajuste:</p>
                <p className="text-slate-600">
                  {reajusteTipo === "global" ? `Todos os ${cargos.filter(c => c.salario_base).length} cargos` : `${selectedCargos.length} cargo(s) selecionado(s)`}
                  {" "}receberao <strong>{reajustePerc}%</strong> de reajuste
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReajuste(false)}>Cancelar</Button>
            <Button onClick={handleReajuste} disabled={applying} className="bg-[#0A1628]" data-testid="btn-aplicar-reajuste">
              {applying ? "Aplicando..." : "Aplicar Reajuste"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CargoForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ nome: "", descricao: "", nivel: 1, salario_base: "", custo_diario: "" });
  const isEdit = !!id;
  const token = sessionStorage.getItem("luvcorp_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (isEdit) {
      axios.get(`${API}/cargos/${id}`, { headers })
        .then(r => setFormData({ nome: r.data.nome, descricao: r.data.descricao || "", nivel: r.data.nivel, salario_base: r.data.salario_base ?? "", custo_diario: r.data.custo_diario ?? "" }))
        .catch(() => { toast.error("Erro"); navigate("/cadastros/cargos"); });
    }
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formData,
        salario_base: formData.salario_base === "" ? null : parseFloat(formData.salario_base),
        custo_diario: formData.custo_diario === "" ? null : parseFloat(formData.custo_diario),
      };
      if (isEdit) {
        await axios.put(`${API}/cargos/${id}`, payload, { headers });
        toast.success("Cargo atualizado!");
      } else {
        await axios.post(`${API}/cargos`, payload, { headers });
        toast.success("Cargo criado!");
      }
      navigate("/cadastros/cargos");
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao salvar")); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-xl mx-auto" data-testid="cargo-form">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/cadastros/cargos"><Button variant="ghost" size="icon"><ArrowLeft className="h-5 w-5" /></Button></Link>
        <h1 className="text-xl font-heading font-bold text-[#0A1628]">{isEdit ? "Editar Cargo" : "Novo Cargo"}</h1>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label>Nome do Cargo *</Label>
              <Input value={formData.nome} onChange={e => setFormData(p => ({ ...p, nome: e.target.value }))} placeholder="Ex: Engenheiro Civil" required data-testid="input-nome" />
            </div>
            <div className="space-y-2">
              <Label>Descricao</Label>
              <Textarea value={formData.descricao} onChange={e => setFormData(p => ({ ...p, descricao: e.target.value }))} placeholder="Responsabilidades do cargo..." rows={3} data-testid="input-descricao" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nivel *</Label>
                <Input type="number" min="1" max="10" value={formData.nivel} onChange={e => setFormData(p => ({ ...p, nivel: parseInt(e.target.value) || 1 }))} required data-testid="input-nivel" />
                <p className="text-xs text-slate-500">1 = Mais alto, 10 = Mais baixo</p>
              </div>
              <div className="space-y-2">
                <Label>Salario Base (R$)</Label>
                <Input type="number" step="0.01" min="0" value={formData.salario_base} onChange={e => setFormData(p => ({ ...p, salario_base: e.target.value }))} placeholder="0,00" data-testid="input-salario-base" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Custo Diário (R$) <span className="text-xs text-slate-500">— usado na Precificação</span></Label>
              <Input
                type="number" step="0.01" min="0"
                value={formData.custo_diario}
                onChange={e => setFormData(p => ({ ...p, custo_diario: e.target.value }))}
                placeholder="Ex: 400,00"
                data-testid="input-custo-diario"
              />
              <p className="text-xs text-slate-500">
                Valor da diária do cargo, obrigatório para uso no módulo de Precificação.
              </p>
            </div>
            <Button type="submit" className="w-full bg-[#0A1628] hover:bg-[#0A1628]/90 h-11" disabled={loading} data-testid="submit-btn">
              {loading ? "Salvando..." : isEdit ? "Atualizar Cargo" : "Criar Cargo"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default function CargosPage() {
  return (
    <MainLayout>
      <Routes>
        <Route index element={<CargosList />} />
        <Route path="new" element={<CargoForm />} />
        <Route path=":id/edit" element={<CargoForm />} />
      </Routes>
    </MainLayout>
  );
}
